import { sendUtagViewEvent } from './viewEvent';
import { sendUtagLinkEvent } from './linkEvent';
import { PricingModel } from '../Product';
import type { AppMode } from 'src/App/context/PricingContextProvider';
import type {
  Brand,
  Country,
  getZoneFromSite,
  Language,
} from '@seek/melways-sites';
import {
  getContractType,
  getCurrentPage,
  getEventName,
  getPricingType,
} from './utils';
import type { AdvertiserContract } from 'src/hooks';
import type { AnalyticsEvent } from '@seek/adv-header-footer';

type Zone = ReturnType<typeof getZoneFromSite>;

interface PageInfo {
  contracts: AdvertiserContract[];
  advertiserId: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  appMode: AppMode;
  pricingModel: PricingModel;
  brand: Brand;
  country: Country;
  zone: Zone;
  language: Language;
}

interface PageEvent {
  advertiserCommitmentType: 'contract' | 'ad-pack' | 'casual';
  pricingType: 'fixed' | 'variable';
  brand: Brand;
  currentPage: 'ad price lookup full' | 'ad price lookup preview';
  hirerUserEmail: string;
  hirerUserFullName: string;
  isLoggedIn: boolean;
  seekAdvertiserId?: string;
  siteCountry: Country;
  siteLanguage: Language;
  siteSection: 'tools';
  userId?: string;
  zone: Zone;
}

const createEventBase = ({
  contracts,
  advertiserId,
  userId,
  userEmail,
  userFullName,
  appMode,
  pricingModel,
  brand,
  country,
  zone,
  language,
}: PageInfo): PageEvent => ({
  advertiserCommitmentType: getContractType(contracts),
  pricingType: getPricingType(pricingModel),
  brand,
  currentPage: getCurrentPage(appMode),
  hirerUserEmail: userEmail,
  hirerUserFullName: userFullName,
  isLoggedIn: userId ? true : false,
  seekAdvertiserId: advertiserId,
  siteCountry: country,
  siteLanguage: language,
  siteSection: 'tools',
  userId,
  zone,
});

// Requirement:
// https://myseek.atlassian.net/browse/AT-3666
// https://myseek.atlassian.net/browse/AT-3693
const sendOnLoadEvent = (data: PageInfo) => {
  const event = {
    ...createEventBase(data),
    eventName: getEventName(data.appMode),
  };

  sendUtagViewEvent(event.eventName, event);
};

// Requirement:
// https://myseek.atlassian.net/browse/AT-3668
// https://myseek.atlassian.net/browse/AT-3694
const sendClassificationSelectedEvent = (data: PageInfo) => {
  const event = {
    ...createEventBase(data),
    eventName: 'form_element_pressed',
    objectInteraction: 'classification',
  };

  sendUtagLinkEvent(event.eventName, event);
};

// Requirement:
// https://myseek.atlassian.net/browse/AT-3667
// https://myseek.atlassian.net/browse/AT-3692
const sendRoleTitleChangedEvent = (data: PageInfo) => {
  const event = {
    ...createEventBase(data),
    eventName: 'form_element_pressed',
    objectInteraction: 'job-title',
  };

  sendUtagLinkEvent(event.eventName, event);
};

// Requirement:
// https://myseek.atlassian.net/browse/AT-3669
// https://myseek.atlassian.net/browse/AT-3695
const sendLocationSelectedEvent = (data: PageInfo) => {
  const event = {
    ...createEventBase(data),
    eventName: 'form_element_pressed',
    objectInteraction: 'granular-location',
  };
  sendUtagLinkEvent(event.eventName, event);
};

const sendProductLinkClickedEvent = (data: PageInfo) => {
  const event = {
    ...createEventBase(data),
    linkName: 'product-page-link-clicked',
    eventName: ['product-page-link-clicked'],
  };
  sendUtagLinkEvent(event.eventName, event);
};

// Requirement:
// https://myseek.atlassian.net/browse/AT-3670
// https://myseek.atlassian.net/browse/AT-3696
const sendPriceQueryEvent = (
  pageInfo: PageInfo,
  locationId: number,
  classification: string,
  subClassification: string,
) => {
  const event = {
    ...createEventBase(pageInfo),
    eventName: 'adprice_query_displayed',
    jobLocationId: locationId,
    jobClassification: classification,
    jobSubClassification: subClassification,
  };
  sendUtagLinkEvent(event.eventName, event);
};

const sendHeaderFooterEvent = (
  pageInfo: PageInfo,
  headerFooterEvent: AnalyticsEvent,
) => {
  const event = {
    ...createEventBase(pageInfo),
    ...headerFooterEvent,
  };
  sendUtagLinkEvent(headerFooterEvent.eventName, event);
};

export {
  type PageInfo,
  sendOnLoadEvent,
  sendClassificationSelectedEvent,
  sendRoleTitleChangedEvent,
  sendLocationSelectedEvent,
  sendProductLinkClickedEvent,
  sendPriceQueryEvent,
  sendHeaderFooterEvent,
};
